import { Fragment, ReactElement } from 'react'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'

import { Checkbox } from 'components/v2/Checkbox'
import { FormattedTimeByDate } from '@10xLabs/react-web/components/FormattedTimeByDate'
import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import { FormattedDate } from '@10xLabs/react-web/components/FormattedDate'
import { FormattedRoute } from '@10xLabs/react-web/components/FormattedRoute'
import { passengerTypeMessages } from 'features/Bookings/components/PassengerType/messages'
import { has } from 'utils/array'
import { sanitizePrice } from 'utils/math'
import { isSameTrip } from 'resolvers/bookings'
import { safeFormatMessage } from 'utils/intl'
import { GTBookingV2, GTBookingV2TicketCancellationSource, GTBookingV2TicketStatus } from 'gql/types.generated'
import { SecondaryInfo } from 'features/App/components/SecondaryInfo'
import { IBookingModel, ITicketModel } from 'features/Bookings/types'

const TripCell = styled.td<TSFixMe>`
  padding-left: 5px;
  padding-right: 5px;
  padding-top: ${(props): TSFixMe => (props.isFirst ? '0' : '10px')};
  padding-bottom: 10px;
`

const TripRow = ({ trip, isReturn }: TSFixMe): TSFixMe => {
  const intl = useIntl()

  return (
    <tr>
      <TripCell colSpan="4">
        <TripDetails>
          {isReturn ? (
            <FormattedMessage
              id="booking.cancellation.partial.return.title"
              defaultMessage="<strong>RETURN -</strong>"
              values={{
                strong: (msg): TSFixMe => <strong>{msg}</strong>
              }}
            />
          ) : (
            <FormattedMessage
              id="booking.cancellation.partial.outgoing.title"
              defaultMessage="<strong>IDA -</strong>"
              values={{
                strong: (msg): TSFixMe => <strong>{msg}</strong>
              }}
            />
          )}
          &nbsp;
          <FormattedRoute origin={trip.segment.origin} destination={trip.segment.destination} />
          &nbsp;&nbsp;&nbsp;
          <FormattedDate
            intl={intl}
            value={trip.segment.departureAt}
            month="2-digit"
            day="2-digit"
            year="numeric"
            timeZone={trip.timezone}
          />
          &nbsp;
          <FormattedTimeByDate intl={intl} date={trip.segment.departureAt} timeZone={trip.timezone || undefined} />
        </TripDetails>
      </TripCell>
    </tr>
  )
}

const TicketCell = styled.td<TSFixMe>`
  padding-top: 20px;
  padding-bottom: 20px;
  padding-left: ${(props): TSFixMe => (props.isFirst ? '5px' : '20px')};
  padding-right: ${(props): TSFixMe => (props.isLast ? '5px' : '20px')};
  color: ${(props): TSFixMe => (props.isCancelled ? '#9999bd' : '#5d5c70')};
`

const StyledTicketRow = styled.tr<TSFixMe>`
  background: ${(props): TSFixMe => (props.isEven ? '#ffffff' : '#f8f9fc')};
`

const PaidAmount = styled.div<TSFixMe>`
  color: #66bb6a;
`

interface ITicketRowProps {
  booking: GTBookingV2
  ticket: ITicketModel
  selectedTicketsIds: string[]
  setSelectedTicketsIds: (ids: string[]) => void
  index: number
}

const TicketRow = ({
  ticket,
  selectedTicketsIds,
  setSelectedTicketsIds,
  index,
  booking
}: ITicketRowProps): ReactElement => {
  const intl = useIntl()
  const { passenger } = ticket

  const handleChange = ({ target: { checked } }: TSFixMe): TSFixMe => {
    if (checked) {
      setSelectedTicketsIds([...selectedTicketsIds, ticket.id])
    } else {
      setSelectedTicketsIds(selectedTicketsIds.filter((id: TSFixMe): TSFixMe => id !== ticket.id))
    }
  }

  const isCancelled = ticket.status === GTBookingV2TicketStatus.Cancelled
  const isHiddenPayment = ticket.paidAmount === 0

  const pricesCurrency = booking.currency.code === 'USD' ? 'USD' : undefined

  return (
    <StyledTicketRow isEven={index % 2 === 0}>
      <TicketCell isFirst isCancelled={isCancelled}>
        {isCancelled ? (
          <>
            <FormattedMessage id="dictionary.cancelled" defaultMessage="Cancelled" />
            {ticket.cancellationSource === GTBookingV2TicketCancellationSource.UserAccount && (
              <SecondaryInfo>
                <FormattedMessage id="bookings.cancel.tickets.cancelledByUser" defaultMessage="(User)" />
              </SecondaryInfo>
            )}
          </>
        ) : (
          <Checkbox id="ticketsIds" checked={has(selectedTicketsIds, ticket.id)} onChange={handleChange} />
        )}
      </TicketCell>
      <TicketCell isCancelled={isCancelled}>
        {passenger?.firstName ? `${passenger.firstName} ${passenger.lastName}` : 'N/A'}
      </TicketCell>
      <TicketCell isCancelled={isCancelled}>
        {safeFormatMessage({ intl, messages: passengerTypeMessages, key: passenger?.type?.name || '' })}
      </TicketCell>
      <TicketCell isLast isCancelled={isCancelled}>
        <FormattedPrice
          value={sanitizePrice(ticket.subtotal + ticket.vat - ticket.discount)}
          currency={pricesCurrency}
        />
      </TicketCell>
      <TicketCell isLast isCancelled={isCancelled}>
        <PaidAmount>
          {isHiddenPayment ? null : (
            <FormattedMessage
              id="booking.cancellation.partial.tickets.paidAmount"
              defaultMessage="Pagó {amount}"
              values={{
                amount: <FormattedPrice value={sanitizePrice(ticket.paidAmount)} currency={pricesCurrency} />
              }}
            />
          )}
        </PaidAmount>
      </TicketCell>
    </StyledTicketRow>
  )
}

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 0 -5px;
`

const TripDetails = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const Split = styled.div`
  width: 100%;
  height: 1px;
  background-color: transparent;
  margin: 10px 0 20px;
`

interface ISelectTicketsProps {
  booking: IBookingModel
  selectedTicketsIds: string[]
  setSelectedTicketsIds: (ids: string[]) => void
}

export const SelectTickets = ({
  booking,
  selectedTicketsIds,
  setSelectedTicketsIds
}: ISelectTicketsProps): ReactElement => {
  const { departureTrips, returnTrips } = booking

  return (
    <Table>
      <tbody>
        {departureTrips.map((trip, index): ReactElement => {
          const tickets = booking.tickets.filter((ticket) => ticket.trip?.id === trip.id)
          return (
            <Fragment key={index}>
              <TripRow trip={trip} isFirst />
              {tickets.map((ticket, index): ReactElement => {
                return (
                  <TicketRow
                    key={ticket.id}
                    ticket={ticket}
                    selectedTicketsIds={selectedTicketsIds}
                    setSelectedTicketsIds={setSelectedTicketsIds}
                    index={index}
                    booking={booking}
                  />
                )
              })}
            </Fragment>
          )
        })}
        {returnTrips.length > 0 && !isSameTrip(departureTrips, returnTrips) && (
          <>
            <tr>
              {/*@ts-expect-error ts(2322) FIXME*/}
              <td colSpan="4">
                <Split />
              </td>
            </tr>
            {booking.returnTrips.map((trip, index): ReactElement => {
              const tickets = booking.tickets.filter((ticket) => ticket.trip?.id === trip.id)
              return (
                <Fragment key={index}>
                  <TripRow trip={trip} isReturn />
                  {tickets.map((ticket, index): ReactElement => {
                    return (
                      <TicketRow
                        key={ticket.id}
                        ticket={ticket}
                        selectedTicketsIds={selectedTicketsIds}
                        setSelectedTicketsIds={setSelectedTicketsIds}
                        index={index}
                        booking={booking}
                      />
                    )
                  })}
                </Fragment>
              )
            })}
          </>
        )}
      </tbody>
    </Table>
  )
}
